import { useState, useEffect, useCallback } from 'react';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { Button } from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import { toast } from 'sonner';
import { getAllFacilities } from '../data/mockAdapter';

// ─── Types ───────────────────────────────────────────────────────────────────

type FacilityStatus = 'active' | 'paused';

interface FacilityOption {
  id: string;
  name: string;
  status: FacilityStatus;
  lastUpdated: string;
}

interface CollateralRow {
  symbol: string;
  haircut: number | '';
  liqThreshold: number | '';
  liqPenalty: number | '';
}

interface ConfigFormValues {
  allowedJurisdictions: Record<string, boolean>;
  restrictedJurisdictions: Record<string, boolean>;
  allowedInvestorTypes: Record<string, boolean>;
  minInvestment: Record<string, number | ''>;
  globalMaxLtv: number | '';
  ltvOverrides: Record<string, number | ''>;
  liquidationThreshold: number | '';
  healthFactorMethod: string;
  oracleProvider: string;
  oracleFrequency: number | '';
  oracleSourceUrl: string;
  collateral: CollateralRow[];
}

// ─── Constants ───────────────────────────────────────────────────────────────

const ADMIN_FACILITIES: FacilityOption[] = getAllFacilities().map((f) => ({
  id: f.id,
  name: f.name,
  status: (f.status === 'shutdown' ? 'paused' : f.status) as FacilityStatus,
  lastUpdated: '20/01/2026, 11:30:00',
}));

const JURISDICTIONS = ['US', 'UK', 'EU', 'UAE', 'Singapore', 'Switzerland'] as const;
const JURISDICTION_LABELS: Record<string, string> = {
  US: 'United States',
  UK: 'United Kingdom',
  EU: 'European Union',
  UAE: 'United Arab Emirates',
  Singapore: 'Singapore',
  Switzerland: 'Switzerland',
};

const INVESTOR_TYPES = ['Retail', 'Accredited', 'Institutional', 'Qualified Purchaser'] as const;

const HEALTH_FACTOR_METHODS = ['Simple', 'Weighted', 'Custom'] as const;
const ORACLE_PROVIDERS = ['Chainlink', 'Uniswap TWAP', 'Manual'] as const;

function buildDefaultConfig(facility: ReturnType<typeof getAllFacilities>[number]): ConfigFormValues {
  return {
    allowedJurisdictions: { US: true, UK: true, EU: true, UAE: false, Singapore: false, Switzerland: false },
    restrictedJurisdictions: { US: false, UK: false, EU: false, UAE: false, Singapore: false, Switzerland: false },
    allowedInvestorTypes: { Retail: false, Accredited: true, Institutional: true, 'Qualified Purchaser': true },
    minInvestment: { Accredited: 50000, Institutional: 500000, 'Qualified Purchaser': 1000000 },
    globalMaxLtv: facility.maxLtvPct,
    ltvOverrides: { Accredited: '', Institutional: facility.maxLtvPct + 5, 'Qualified Purchaser': facility.maxLtvPct + 10 },
    liquidationThreshold: facility.liquidationThresholdPct,
    healthFactorMethod: 'Weighted',
    oracleProvider: 'Chainlink',
    oracleFrequency: 15,
    oracleSourceUrl: '',
    collateral: facility.collateralTokens.map((token) => ({
      symbol: token,
      haircut: 15,
      liqThreshold: 85,
      liqPenalty: 5,
    })),
  };
}

const FACILITY_CONFIGS: Record<string, ConfigFormValues> = Object.fromEntries(
  getAllFacilities().map((f) => [f.id, buildDefaultConfig(f)]),
);

const STATUS_CONFIG: Record<FacilityStatus, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-green-100 text-green-700' },
  paused: { label: 'Paused', className: 'bg-yellow-100 text-yellow-700' },
};

const INPUT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

// ─── Accordion Section ───────────────────────────────────────────────────────

function AccordionSection({
  title,
  isOpen,
  onToggle,
  children,
}: {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-5 py-4 sm:px-6 text-left hover:bg-accent/50 transition-colors rounded-xl"
      >
        <h3 className="text-base sm:text-lg font-semibold">{title}</h3>
        {isOpen ? <Icon name="streamline:interface-arrows-button-up-arrow-up-keyboard" className="h-5 w-5 text-muted-foreground" /> : <Icon name="streamline:interface-arrows-button-down-arrow-down-keyboard" className="h-5 w-5 text-muted-foreground" />}
      </button>
      {isOpen && <div className="border-t border-border px-5 py-5 sm:px-6 sm:py-6">{children}</div>}
    </div>
  );
}

function SubAccordionSection({
  title,
  isOpen,
  onToggle,
  children,
}: {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-5 py-4 sm:px-5 text-left hover:bg-accent/50 transition-colors rounded-lg"
      >
        <h3 className="text-sm sm:text-base font-semibold">{title}</h3>
        {isOpen ? <Icon name="streamline:interface-arrows-button-up-arrow-up-keyboard" className="h-4 w-4 text-muted-foreground" /> : <Icon name="streamline:interface-arrows-button-down-arrow-down-keyboard" className="h-4 w-4 text-muted-foreground" />}
      </button>
      {isOpen && <div className="border-t border-border px-5 py-5 sm:px-5">{children}</div>}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function CreditFacilityConfigPage() {
  const [selectedFacilityId, setSelectedFacilityId] = useState(ADMIN_FACILITIES[0].id);
  const [sections, setSections] = useState({
    compliance: true,
    jurisdictions: true,
    investorTypes: true,
    ltvLimits: true,
    dataModules: true,
  });
  const selectedFacility = ADMIN_FACILITIES.find((f) => f.id === selectedFacilityId)!;
  const status = STATUS_CONFIG[selectedFacility.status];

  const { register, control, reset, getValues, formState: { isDirty } } = useForm<ConfigFormValues>({
    defaultValues: FACILITY_CONFIGS[selectedFacilityId],
  });

  const { fields: collateralFields, append: appendCollateral, remove: removeCollateral } = useFieldArray({
    control,
    name: 'collateral',
  });

  const loadFacilityConfig = useCallback(
    (facilityId: string) => {
      const config = FACILITY_CONFIGS[facilityId];
      reset(config);
    },
    [reset],
  );

  useEffect(() => {
    loadFacilityConfig(selectedFacilityId);
  }, [selectedFacilityId, loadFacilityConfig]);

  const toggleSection = (key: keyof typeof sections) => {
    setSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleFacilityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedFacilityId(e.target.value);
  };

  const handleReset = () => {
    const config = FACILITY_CONFIGS[selectedFacilityId];
    reset(config);
  };

  const handleSave = () => {
    reset(getValues(), { keepValues: true });
    toast.success('Configuration saved successfully.');
  };

  const watchedInvestorTypes = useWatch({ control, name: 'allowedInvestorTypes' });
  const enabledInvestorTypes = INVESTOR_TYPES.filter((t) => watchedInvestorTypes?.[t]);

  return (
    <div className="space-y-6 sm:space-y-8 pb-24">
      {/* Page header + Facility selector */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <Icon name="streamline:interface-setting-cog-work-loading-cog-gear-settings-machine" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Configuration</h1>
            <p className="text-sm text-muted-foreground">
              Manage compliance rules and risk parameters for credit facilities.
            </p>
          </div>
        </div>
        <div className="w-full sm:w-auto">
          <label className="block text-sm font-medium mb-1.5">Select Facility</label>
          <select className={`${INPUT_CLASS} sm:w-72`} value={selectedFacilityId} onChange={handleFacilityChange}>
            {ADMIN_FACILITIES.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Facility info bar */}
      <div className="rounded-xl border border-border px-5 py-4 sm:px-6 sm:py-5 flex flex-wrap items-center gap-x-8 gap-y-2">
        <div>
          <span className="text-xs text-muted-foreground">Facility ID</span>
          <p className="text-sm font-semibold">{selectedFacility.id}</p>
        </div>
        <div>
          <span className="text-xs text-muted-foreground">Status</span>
          <p>
            <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${status.className}`}>
              {status.label}
            </span>
          </p>
        </div>
        <div>
          <span className="text-xs text-muted-foreground">Last Updated</span>
          <p className="text-sm font-semibold">{selectedFacility.lastUpdated}</p>
        </div>
      </div>

      {/* Compliance Rules */}
      <AccordionSection title="Compliance Rules" isOpen={sections.compliance} onToggle={() => toggleSection('compliance')}>
        <div className="space-y-4">
          <h3 className="text-lg sm:text-xl font-bold">Compliance Rules</h3>

          {/* Jurisdictions */}
          <SubAccordionSection title="Jurisdictions" isOpen={sections.jurisdictions} onToggle={() => toggleSection('jurisdictions')}>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold mb-3">Allowed Jurisdictions</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {JURISDICTIONS.map((j) => (
                    <label key={`allowed-${j}`} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        {...register(`allowedJurisdictions.${j}`)}
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                      />
                      {JURISDICTION_LABELS[j]}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold mb-3">Restricted/Blacklisted Jurisdictions</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {JURISDICTIONS.map((j) => (
                    <label key={`restricted-${j}`} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        {...register(`restrictedJurisdictions.${j}`)}
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                      />
                      {JURISDICTION_LABELS[j]}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </SubAccordionSection>

          {/* Investor Types */}
          <SubAccordionSection title="Investor Types" isOpen={sections.investorTypes} onToggle={() => toggleSection('investorTypes')}>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold mb-3">Allowed Investor Types</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {INVESTOR_TYPES.map((t) => (
                    <label key={t} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        {...register(`allowedInvestorTypes.${t}`)}
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                      />
                      {t}
                    </label>
                  ))}
                </div>
              </div>
              {enabledInvestorTypes.length > 0 && (
                <div>
                  <p className="text-sm font-semibold mb-3">Minimum Investment by Type</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {enabledInvestorTypes.map((t) => (
                      <div key={`min-${t}`}>
                        <label className="block text-sm font-medium mb-1.5">{t}</label>
                        <input
                          type="number"
                          className={INPUT_CLASS}
                          placeholder="Min amount"
                          {...register(`minInvestment.${t}`, { valueAsNumber: true })}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </SubAccordionSection>
        </div>
      </AccordionSection>

      {/* LTV Limits */}
      <AccordionSection title="LTV Limits" isOpen={sections.ltvLimits} onToggle={() => toggleSection('ltvLimits')}>
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium mb-1.5">Global Maximum LTV (%)</label>
            <input
              type="number"
              className={INPUT_CLASS}
              placeholder="e.g. 75"
              {...register('globalMaxLtv', { valueAsNumber: true })}
            />
          </div>

          <div>
            <p className="text-sm font-semibold mb-3">Per-Investor-Type LTV Overrides</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(['Accredited', 'Institutional', 'Qualified Purchaser'] as const).map((t) => (
                <div key={`ltv-${t}`}>
                  <label className="block text-sm font-medium mb-1.5">{t}</label>
                  <input
                    type="number"
                    className={INPUT_CLASS}
                    placeholder="Max 75%"
                    {...register(`ltvOverrides.${t}`, { valueAsNumber: true })}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5">Liquidation Threshold (%)</label>
            <input
              type="number"
              className={INPUT_CLASS}
              placeholder="e.g. 80"
              {...register('liquidationThreshold', { valueAsNumber: true })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5">Health Factor Calculation Method</label>
            <select className={INPUT_CLASS} {...register('healthFactorMethod')}>
              {HEALTH_FACTOR_METHODS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
        </div>
      </AccordionSection>

      {/* Data Modules & Risk Parameters */}
      <AccordionSection title="Data Modules & Risk Parameters" isOpen={sections.dataModules} onToggle={() => toggleSection('dataModules')}>
        <div className="space-y-6">
          <h3 className="text-lg sm:text-xl font-bold">Data Modules</h3>

          {/* Oracle Settings */}
          <div>
            <h4 className="text-base font-semibold mb-4">Oracle Settings</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Price Oracle Provider</label>
                <select className={INPUT_CLASS} {...register('oracleProvider')}>
                  {ORACLE_PROVIDERS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Update Frequency (minutes)</label>
                <input
                  type="number"
                  className={INPUT_CLASS}
                  {...register('oracleFrequency', { valueAsNumber: true })}
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium mb-1.5">Source URL (Optional)</label>
              <input
                type="text"
                className={INPUT_CLASS}
                placeholder="https://..."
                {...register('oracleSourceUrl')}
              />
            </div>
          </div>

          {/* Collateral Haircuts & Limits */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base font-semibold">Collateral Haircuts &amp; Limits</h4>
              <Button
                variant="outline"
                size="sm"
                onClick={() => appendCollateral({ symbol: '', haircut: '', liqThreshold: '', liqPenalty: '' })}
              >
                <Icon name="streamline:interface-add-1-expand-cross-buttons-button-more-remove-plus-add" className="mr-2 h-4 w-4" /> Add Token
              </Button>
            </div>
            <div className="space-y-3">
              {collateralFields.map((field, index) => (
                <div key={field.id} className="rounded-lg border border-border p-5">
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium mb-1.5">Token Symbol</label>
                      <input
                        type="text"
                        className={INPUT_CLASS}
                        placeholder="e.g. WETH"
                        {...register(`collateral.${index}.symbol`)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium mb-1.5">Haircut (%)</label>
                      <input
                        type="number"
                        className={INPUT_CLASS}
                        {...register(`collateral.${index}.haircut`, { valueAsNumber: true })}
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium mb-1.5">Liq. Threshold (%)</label>
                      <input
                        type="number"
                        className={INPUT_CLASS}
                        {...register(`collateral.${index}.liqThreshold`, { valueAsNumber: true })}
                      />
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <label className="block text-xs sm:text-sm font-medium mb-1.5">Liq. Penalty (%)</label>
                        <input
                          type="number"
                          className={INPUT_CLASS}
                          {...register(`collateral.${index}.liqPenalty`, { valueAsNumber: true })}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeCollateral(index)}
                        className="mb-0.5 rounded-md p-2 text-red-400 hover:text-red-600 transition-colors"
                      >
                        <Icon name="streamline:interface-delete-bin-5-remove-delete-empty-bin-trash-garbage" className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </AccordionSection>

      {/* Sticky footer */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background">
        <div className="mx-auto flex max-w-[1400px] items-center justify-end gap-3 px-4 py-3 sm:px-6">
          <Button variant="outline" onClick={handleReset}>
            <Icon name="streamline:interface-delete-2-remove-bold-add-button-buttons-delete" className="mr-2 h-4 w-4" /> Reset
          </Button>
          <Button disabled={!isDirty} onClick={handleSave}>
            <Icon name="streamline:interface-content-save-disk-floppy-electronics-device-disc-computer" className="mr-2 h-4 w-4" /> Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}
